import { useOktaAuth } from "@okta/okta-react";
import axios from "axios";
import { createContext, ReactNode, useContext, useEffect } from "react";
import {
  AxiosInstanceWithIndexSignature,
  AxiosInstanceWithRetries,
  deleteWithRetries,
  getWithRetries,
  patchWithRetries,
  postWithRetries,
  putWithRetries,
} from "../../api/api";
import { DELEGATE_USER_HEADER_EXCLUDED_SERVICE_URL_KEYS } from "../../constant";
import { ConfigContext } from "../ConfigContext";
import { DelegateContext } from "../DelegateContext/DelegateContext";

const ID_TOKEN_HEADER = "X-id-token";
export const DELEGATE_USER_HEADER = "X-Delegate-User";

const attachAxiosInstanceHandlers = (
  instance: AxiosInstanceWithIndexSignature
): AxiosInstanceWithRetries =>
  ({
    ...instance,
    getWithRetries: (url, config) => getWithRetries(url, config, instance),
    postWithRetries: (url, body, config) =>
      postWithRetries(url, body, config, instance),
    patchWithRetries: (url, body, config) =>
      patchWithRetries(url, body, config, instance),
    putWithRetries: (url, body, config) =>
      putWithRetries(url, body, config, instance),
    deleteWithRetries: (url, config) =>
      deleteWithRetries(url, config, instance),
  } as AxiosInstanceWithRetries);

const createAxiosInstance = () => {
  const instance: AxiosInstanceWithIndexSignature = axios.create({
    timeout: 300000,
    headers: {
      "Content-Type": "application/json",
    },
  });

  return attachAxiosInstanceHandlers(instance);
};

export const ApiContext = createContext<AxiosInstanceWithRetries>(
  createAxiosInstance()
);

export const ApiContextProvider = ({ children }: { children: ReactNode }) => {
  const { authState } = useOktaAuth();
  const appConfig = useContext(ConfigContext);
  const { ENABLE_DELEGATE_LOGIN } = appConfig;
  const { isDelegating, delegatedUserId } = useContext(DelegateContext);

  const axiosInstance = createAxiosInstance();

  useEffect(() => {
    const requestInterceptor = axiosInstance.interceptors.request.use(
      (request) => {
        const updatedRequest = { ...request };
        updatedRequest.headers.Authorization = `Bearer ${authState?.accessToken?.accessToken}`;
        updatedRequest.headers[ID_TOKEN_HEADER] = authState?.idToken?.idToken;
        if (
          ENABLE_DELEGATE_LOGIN &&
          isDelegating &&
          !DELEGATE_USER_HEADER_EXCLUDED_SERVICE_URL_KEYS.some((serviceKey) =>
            request.url?.startsWith(appConfig[serviceKey])
          )
        ) {
          updatedRequest.headers[DELEGATE_USER_HEADER] = delegatedUserId;
        }
        return updatedRequest;
      }
    );

    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor);
    };
  }, [
    authState,
    ENABLE_DELEGATE_LOGIN,
    appConfig,
    isDelegating,
    delegatedUserId,
  ]);

  return (
    <ApiContext.Provider value={axiosInstance}>{children}</ApiContext.Provider>
  );
};
